import React, { useRef, useEffect, useState } from "react";
import {
  timeFormat,
  select,
  line,
  curveLinear,
  scaleLinear,
  axisBottom,
  axisLeft,
} from "d3";
import TurbineAnimation from "../../../components/turbine-animation/TurbineAnimation";
import WplSwitch from "../../../components/wpl-switch/wplSwitch";
import "./risk-timeline-graph.css";

export default function RiskTimelineGraph({ riskTimeline, turbine }) {
  const svgRef = useRef();
  const riskGraphRef = useRef();
  const [noData, setNoData] = useState(false);
  const [visibleTurbines, setVisibleTurbines] = useState(turbine);

  const colorPalette = [
    "#000000", "#f44336", "#2196f3", "#4caf50",
    "#ff9800", "#9c27b0", "#00bcd4", "#e91e63",
    "#8bc34a", "#ffeb3b"
  ];

  const turbineColors = {};
  (Array.isArray(turbine) ? turbine : [turbine]).forEach((t, i) => {
    turbineColors[t.name] = colorPalette[i % colorPalette.length];
  });

  useEffect(() => {
    setVisibleTurbines(turbine);
  }, [turbine]);

  const toggleTurbine = (name) => {
    setVisibleTurbines((prev) => {
      const exists = prev.some((t) => t.name === name);
      if (exists) {
        return prev.filter((t) => t.name !== name);
      }
      const tToAdd = (Array.isArray(turbine) ? turbine : [turbine]).find(
        (t) => t.name === name
      );
      return tToAdd ? [...prev, tToAdd] : prev;
    });
  };

  useEffect(() => {
    if (
      !svgRef.current ||
      !riskGraphRef.current ||
      !riskTimeline ||
      !turbine ||
      !visibleTurbines
    )
      return;

    if (!riskTimeline.lines || riskTimeline.lines.length === 0) {
      return;
    }

    const svg = select(svgRef.current);
    const svgWrapper = select(riskGraphRef.current);

    const pixelWidth = 900;
    const pixelHeight = 350;

    const turbines = Array.isArray(visibleTurbines)
      ? visibleTurbines
      : [visibleTurbines];

    const graphs = turbines
      .map((t) => riskTimeline.lines.find((line) => line.name === t.name))
      .filter(Boolean);

    if (graphs.length === 0) {
      setNoData(true);
      select(svgRef.current).selectAll("*").remove();
      return;
    }

    setNoData(false);

    svg.selectAll("*").remove();
    svgWrapper.selectAll(".zone, .tooltip, .vertical, .horizontal").remove();


    //scales

    const allGraphs = (Array.isArray(turbine) ? turbine : [turbine])
      .map((t) => riskTimeline.lines.find((line) => line.name === t.name))
      .filter(Boolean);

    const maxY = Math.max(...allGraphs.map((g) => g.max_y)) * 1.2;

    const xScale = scaleLinear()
      .domain([new Date(riskTimeline.min_x), new Date(riskTimeline.max_x)])
      .range([0, pixelWidth]);


    const yScale = scaleLinear()
      .domain([riskTimeline.min_y, maxY])
      .range([pixelHeight, 0]);

    //axes
    const xAxis = axisBottom(xScale)
      .ticks(7)
      .tickFormat(timeFormat("%b %d"));


    const yAxis = axisLeft(yScale);

    //line generator
    const dLine = line()
      .x((d) => xScale(new Date(d.x)))
      .y((d) => yScale(d.y))
      .curve(curveLinear);

    graphs.forEach((g) => {
      svg
        .append("path")
        .datum(g.data)
        .attr("fill", "none")
        .attr("stroke", turbineColors[g.name])
        .attr("stroke-width", "2px")
        .attr("d", dLine);
    });

    //Tooltip
    const tooltip = svgWrapper
      .append("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("pointer-events", "none")
      .style("background", "white")
      .style("border", "1px solid black")
      .style("padding", "4px 8px")
      .style("border-radius", "4px")
      .style("display", "none");

    //draw X axis
    svg
      .selectAll(".x-axis")
      .data([0])
      .join("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0, ${pixelHeight})`)
      .call(xAxis);

    //draw Y axis
    svg
      .selectAll(".y-axis")
      .data([0])
      .join("g")
      .attr("class", "y-axis")
      .call(yAxis);

    // display vertical and horizontal guide lines
    svgWrapper
      .append("div")
      .attr("class", "vertical")
      .style("pointer-events", "none")
      .style("position", "absolute")
      .style("width", "1px")
      .style("top", "0px")
      .style("bottom", "50px")
      .style("display", "none")
      .style("background", "black");

    svgWrapper
      .append("div")
      .attr("class", "horizontal")
      .style("pointer-events", "none")
      .style("position", "absolute")
      .style("height", "1px")
      .style("left", "0px")
      .style("right", "50px")
      .style("display", "none")
      .style("background", "black");

    // draw zones
    const zones = [0.3, 0.6, maxY];
    const zoneColors = [
      "rgba(136, 177, 139, 0.3)",
      "rgba(230, 157, 115, 0.3)",
      "rgba(212, 106, 106, 0.3)",
    ];

    let prevZone = riskTimeline.min_y;
    zones.forEach((zone, idx) => {
      svgWrapper
        .append("div")
        .attr("class", "zone")
        .style("position", "absolute")
        .style("width", pixelWidth + "px")
        .style("height", yScale(prevZone) - yScale(zone) + "px")
        .style("top", yScale(zone) + "px")
        .style("left", "0px")
        .style("background", zoneColors[idx]);
      prevZone = zone;
    });

    svgWrapper.on("mousemove", (e) => {
      const boundingRect = svgWrapper.node().getBoundingClientRect();
      const mouseX = e.clientX - boundingRect.left;
      const mouseY = e.clientY - boundingRect.top;
      const chartTop = 0;
      const chartBottom = pixelHeight;
      const chartLeft = 0;
      const chartRight = pixelWidth;

      if (
        mouseX < chartLeft ||
        mouseX > chartRight ||
        mouseY < chartTop ||
        mouseY > chartBottom
      ) {
        tooltip.style("display", "none");
        svgWrapper.selectAll(".vertical").style("display", "none");
        svgWrapper.selectAll(".horizontal").style("display", "none");
        return;
      }

      const clampedX = Math.max(chartLeft, Math.min(mouseX, chartRight));
      const clampedY = Math.max(chartTop, Math.min(mouseY, chartBottom));

      const xHover = xScale.invert(clampedX);
      const hoverY = yScale.invert(clampedY);

      let closest = null;
      let minDistance = Infinity;

      graphs.forEach((g) => {
        const data = g.data;
        if (!data.length) return;

        let i = data.findIndex((d) => new Date(d.x) > xHover);
        if (i === -1) i = data.length - 1;
        else if (i === 0) i = 1;

        const p1 = data[i - 1];
        const p2 = data[i] || data[i - 1];
        const x1 = new Date(p1.x).getTime();
        const x2 = new Date(p2.x).getTime();
        const y1 = p1.y;
        const y2 = p2.y;
        const t = x2 === x1 ? 0 : (xHover - x1) / (x2 - x1);
        const closestY = y1 + (y2 - y1) * t;

        const distance = Math.abs(mouseY - yScale(closestY));
        if (distance < minDistance) {
          minDistance = distance;
          closest = { graph: g, point: { x: new Date(p1.x), y: closestY } };
        }
      });

      if (closest) {
        const tooltipWidth = 180;
        const offset = 10;
        const tooltipX = Math.min(clampedX + offset, chartRight - tooltipWidth);
        const tooltipY = clampedY + offset;

        tooltip
          .style("display", "block")
          .style("left", tooltipX + "px")
          .style("top", tooltipY + "px")
          .html(
            `<strong>Turbine: ${closest.graph.name}</strong><br/>
            ${timeFormat("%b %d %H:%M")(xHover)}<br/>
              Value: ${hoverY.toFixed(2)}`
          );

        svgWrapper
          .selectAll(".vertical")
          .style("left", xScale(xHover) + "px")
          .style("display", "block");

        svgWrapper
          .selectAll(".horizontal")
          .style("top", yScale(hoverY) + "px")
          .style("display", "block");
      }
    });


    svgWrapper.on("mouseleave", () => {
      tooltip.style("display", "none");
      svgWrapper.selectAll(".vertical").style("display", "none");
      svgWrapper.selectAll(".horizontal").style("display", "none");
    });

    return () => {
      svgWrapper.selectAll(".vertical").remove();
      svgWrapper.selectAll(".horizontal").remove();
      svg.selectAll("*").remove();
      svgWrapper.selectAll(".zone").remove();
      svgWrapper.selectAll(".tooltip").remove();
    };
  }, [riskTimeline, turbine, visibleTurbines]);

  return (
    <div className="risk-graph-card">
      {riskTimeline ? (
        <>

          <div className="risk-graph-area">

            <div className="risk-graph" ref={riskGraphRef} style={{ position: "relative" }}>
              <svg ref={svgRef}></svg>
              {noData && (
                <div
                  className="no-data-overlay"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    background: "rgba(255, 255, 255, 0.7)",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  No data available for the selected turbine.
                </div>
              )}

              <div className="line-legend">
                <div className="line-legend-item" title="Your theoretical risk">
                  <div className="line-legend-color" style={{ background: "black" }} />
                  <label>Actual risk</label>
                </div>
                <div className="zone-legend-item" title="High risk zone">
                  <div className="zone-legend-color" style={{ background: "rgba(212, 106, 106, 0.8)" }} />
                  <label>High risk zone</label>
                </div>
                <div className="zone-legend-item" title="Medium risk zone">
                  <div className="zone-legend-color" style={{ background: "rgba(230, 157, 115, 0.8)" }} />
                  <label>Medium risk zone</label>
                </div>
                <div className="zone-legend-item" title="Low risk zone">
                  <div className="zone-legend-color" style={{ background: "rgba(136, 177, 139, 0.8)" }} />
                  <label>Low risk zone</label>
                </div>
              </div>
            </div>


            {turbine.length !== 1 && (
              <div className="risk-turbine-selector-wrapper">
                <h4 className="turbine-selector-title">Turbines</h4>
                <div className="turbine-grid">
                  {(Array.isArray(turbine) ? turbine : [turbine]).map((t) => (
                    <div key={t.name} className="turbine-legend-item">
                      <div className="turbine-info">
                        <span
                          className="turbine-color"
                          style={{ background: turbineColors[t.name] }}
                        />
                        <span className="turbine-name">{t.name}</span>
                      </div>
                      <WplSwitch
                        value={visibleTurbines.some((vt) => vt.name === t.name)}
                        onChanged={() => toggleTurbine(t.name)}
                        className="setting-input"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="risk-graph-info">
            <p>
              Displays your running risk. The total risk is the theoretical risk of lightning strikes on the selected turbine.
            </p>
            <p>
              The y-axis is a measure of the accumulated risk of lightning strikes within the collection area of the turbine.
            </p>
            <p>To lower your 'actual risk', mark turbines as inspected.</p>
            <p>
              We call this number <abbr title="Theoretical Lightning Attachment">TLA</abbr>
            </p>
          </div>
        </>
      ) : (
        <div className="spinner-area">
          <TurbineAnimation />
        </div>
      )}
    </div>
  );
}
